import {ottopilotEventContainersWorkspaceCapabilityConfigurationValidator} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainersWorkspaceCapabilityConfiguration";
import {isMovementCombinationSupported, type MovementTemplate} from "./transferLocation";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import {WorkspaceCapabilityStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {EventContainerMovementOperationConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/eventContainerMovementWorkspaceCapability/domain/EventContainerMovementWorkspaceCapabilityConfiguration";
import {eventContainerMovementWorkspaceCapabilityConfigurationValidator} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/eventContainerMovementWorkspaceCapability/domain/EventContainerMovementWorkspaceCapabilityConfiguration";
import {PageType} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import type {WorkspaceConfig, WorkspaceTransferUiConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {resolveTransferUiConfig} from "~/domainModels/transfer/defaultTransferConfig";
import type {TransferFieldRef, WorkspaceTransferConfig} from "~/domainModels/transfer/types";

export function getEnabledEventContainerMovementOperations(
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>,
): Array<EventContainerMovementOperationConfiguration> {
    const capability = workspaceCapabilities.find(
        (candidate) => candidate.type === WorkspaceCapabilityType.eventContainerMovement && candidate.status === WorkspaceCapabilityStatus.enabled,
    );
    if (capability === undefined) {
        return [];
    }
    const configurationResult = eventContainerMovementWorkspaceCapabilityConfigurationValidator.safeParse(capability.configuration);
    if (configurationResult.success !== true) {
        return [];
    }
    const containerCapability = workspaceCapabilities.find(
        (candidate) => candidate.type === WorkspaceCapabilityType.ottopilotEventContainers && candidate.status === WorkspaceCapabilityStatus.enabled,
    );
    if (containerCapability?.type !== WorkspaceCapabilityType.ottopilotEventContainers) {
        return configurationResult.data.operations;
    }
    const containerConfiguration = ottopilotEventContainersWorkspaceCapabilityConfigurationValidator.safeParse(containerCapability.configuration);
    if (!containerConfiguration.success) {
        return [];
    }
    const templates = Object.entries(containerConfiguration.data.eventContainerTemplates).map(([id, template]) => ({
        id: id as Uuid,
        configuration: template.configuration,
    }));
    return configurationResult.data.operations.filter((operation) =>
        operation.allowedFieldCombinations.some((combination) => isMovementCombinationSupported(operation, combination, templates)),
    );
}

function buildTransferConfigFromUiAndOperations(
    transferUi: Partial<WorkspaceTransferUiConfig> | undefined,
    operations: Array<EventContainerMovementOperationConfiguration>,
): WorkspaceTransferConfig {
    const ui = resolveTransferUiConfig(transferUi);
    const statusRule = operations.find((operation) => operation.businessStatusRule !== undefined)?.businessStatusRule;
    const requiredRoleTemplateIds = [...new Set(operations.flatMap((operation) => operation.actorUserTemplateIds ?? []))];
    const allowedReasonIds = [...new Set(operations.flatMap((operation) => operation.reason.allowedValues ?? []))];
    const reasons =
        allowedReasonIds.length === 0
            ? ui.reasons
            : allowedReasonIds.map((reasonId) => ({
                  id: reasonId,
                  label: ui.reasons.find((reason) => reason.id === reasonId)?.label ?? reasonId,
              }));

    return {
        ...ui,
        reasons,
        dimensions: [],
        eventContainerTemplateIds: [...new Set(operations.map((operation) => operation.eventContainerTemplateId))],
        operationalChange: {
            module: "journey",
            statusColumnId: statusRule?.columnId,
            statusRules:
                statusRule === undefined
                    ? []
                    : statusRule.eligibleValues.map((statusId) => ({
                          statusId,
                          available: true,
                      })),
            requiredRoleTemplateIds: requiredRoleTemplateIds.length === 0 ? undefined : requiredRoleTemplateIds,
            supportsMovement: true,
        },
        ineligibleStatusColumnId: statusRule?.columnId,
        ineligibleStatusIds: [],
    };
}

export function getTransferConfigForCapabilities(
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>,
    transferUi?: Partial<WorkspaceTransferUiConfig>,
): WorkspaceTransferConfig | null {
    const operations = getEnabledEventContainerMovementOperations(workspaceCapabilities);
    if (operations.length === 0) {
        return null;
    }
    return buildTransferConfigFromUiAndOperations(transferUi, operations);
}

export function applyMovementOperationsToTransferConfig(
    transferConfig: WorkspaceTransferConfig,
    operations: Array<EventContainerMovementOperationConfiguration>,
    templates?: ReadonlyArray<MovementTemplate>,
): WorkspaceTransferConfig {
    const dimensions = operations
        .flatMap((operation) =>
            operation.allowedFieldCombinations.map((combination, combinationIndex) => {
                if (templates !== undefined && !isMovementCombinationSupported(operation, combination, templates)) {
                    return null;
                }
                const requestedFields = [...combination].sort((left, right) => String(left).localeCompare(String(right)));
                const configuredAssignmentColumnIds = new Set(operation.assignmentColumnIds);
                const assignmentColumnIds = requestedFields.filter((field) => configuredAssignmentColumnIds.has(field));
                const organizationalUnitColumnId = requestedFields.find((field) => !configuredAssignmentColumnIds.has(field));
                const includesOrganizationalUnit = organizationalUnitColumnId !== undefined;
                const primaryField: TransferFieldRef | undefined = organizationalUnitColumnId ?? assignmentColumnIds[0];
                if (primaryField === undefined) {
                    return null;
                }
                const fieldLabel = requestedFields
                    .map((field) =>
                        field === organizationalUnitColumnId ? "Organizational Unit" : (operation.frontend.assignmentFieldLabels?.[field] ?? "Assignment"),
                    )
                    .join(" + ");
                const hasMultipleCombinations = operation.allowedFieldCombinations.length > 1;
                return {
                    assignmentColumnIds: assignmentColumnIds,
                    entityType: includesOrganizationalUnit ? ("ou" as const) : ("user" as const),
                    fromField: primaryField,
                    id:
                        operation.frontend.transferDimensionId !== undefined && hasMultipleCombinations !== true
                            ? operation.frontend.transferDimensionId
                            : `${operation.id}:${combinationIndex}`,
                    label: hasMultipleCombinations ? `${operation.displayName} — ${fieldLabel}` : operation.displayName,
                    operationConfigurationId: operation.id,
                    requestedFields: requestedFields,
                    sourceLabel: operation.frontend.sourceLabel ?? "From",
                    targetLabel: operation.frontend.targetLabel ?? "To",
                    toField: primaryField,
                };
            }),
        )
        .filter((dimension) => dimension !== null)
        .sort((left, right) => {
            const leftOperation = operations.find((operation) => operation.id === left.operationConfigurationId);
            const rightOperation = operations.find((operation) => operation.id === right.operationConfigurationId);
            const orderDifference = (leftOperation?.frontend.displayOrder ?? 0) - (rightOperation?.frontend.displayOrder ?? 0);
            return orderDifference === 0 ? left.label.localeCompare(right.label) : orderDifference;
        });
    return {
        ...transferConfig,
        dimensions: dimensions,
        showDimensionSelector: transferConfig.showDimensionSelector === false ? false : dimensions.length > 1,
    };
}

/** Detect transfer-capable manifests before workspace capabilities are available. */
export function isTransferCapableManifest(manifest: {pages: Record<string, {type?: string}>}): boolean {
    return Object.values(manifest.pages).some((page) => page.type === PageType.EventContainerWithEvents);
}

export function isTransferEnabledForEventContainerTemplate(
    transferConfig: WorkspaceTransferConfig | null,
    eventContainerTemplateId: Uuid | null | undefined,
): boolean {
    if (transferConfig === null) {
        return false;
    }
    if (eventContainerTemplateId === null || eventContainerTemplateId === undefined) {
        return false;
    }
    return transferConfig.eventContainerTemplateIds.some((templateId) => String(templateId) === String(eventContainerTemplateId));
}

export function getTransferConfigForWorkspace(args: {
    config: WorkspaceConfig;
    workspaceCapabilities?: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
    eventContainerTemplateId?: Uuid | null;
}): WorkspaceTransferConfig | null {
    if (args.workspaceCapabilities === undefined) {
        return null;
    }
    const transferConfig = getTransferConfigForCapabilities(args.workspaceCapabilities, args.config.transfer);
    if (transferConfig === null) {
        return null;
    }
    if (args.eventContainerTemplateId === undefined) {
        return transferConfig;
    }
    if (isTransferEnabledForEventContainerTemplate(transferConfig, args.eventContainerTemplateId) !== true) {
        return null;
    }
    return transferConfig;
}
